import { CalendarEvent, CalendarEventColor } from "./types";
import { supabase } from "@/integrations/supabase/client";

const LEGACY_LOCAL_KEY = "calendario_eventos";
const MIGRATION_FLAG = "calendario_eventos_migrado";

type Row = {
  id: string;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  color: string;
};

function rowToEvent(r: Row): CalendarEvent {
  return {
    id: r.id,
    title: r.title,
    description: r.description ?? undefined,
    startDate: r.start_date,
    endDate: r.end_date,
    startTime: (r.start_time || "00:00").slice(0, 5),
    endTime: (r.end_time || "00:00").slice(0, 5),
    color: (r.color as CalendarEventColor) || "blue",
  };
}

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

export async function loadEvents(): Promise<CalendarEvent[]> {
  const uid = await currentUserId();
  if (!uid) return [];
  const { data, error } = await supabase
    .from("calendar_events")
    .select("id,title,description,start_date,end_date,start_time,end_time,color")
    .eq("user_id", uid)
    .order("start_date", { ascending: true });
  if (error) throw error;
  return (data as Row[] | null)?.map(rowToEvent) ?? [];
}

export async function saveEvent(event: CalendarEvent): Promise<CalendarEvent> {
  const uid = await currentUserId();
  if (!uid) throw new Error("Usuário não autenticado");
  const payload = {
    id: event.id,
    user_id: uid,
    title: event.title,
    description: event.description ?? null,
    start_date: event.startDate,
    end_date: event.endDate,
    start_time: event.startTime,
    end_time: event.endTime,
    color: event.color,
  };
  const { data, error } = await supabase
    .from("calendar_events")
    .upsert(payload, { onConflict: "id" })
    .select("id,title,description,start_date,end_date,start_time,end_time,color")
    .single();
  if (error) throw error;
  return rowToEvent(data as Row);
}

export async function deleteEvent(id: string): Promise<void> {
  const { error } = await supabase.from("calendar_events").delete().eq("id", id);
  if (error) throw error;
}

function readLegacyLocal(): CalendarEvent[] {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (e) => e && e.id && e.title && e.startDate && e.endDate,
    ) as CalendarEvent[];
  } catch {
    return [];
  }
}

/**
 * One-shot migration of legacy localStorage events into Supabase.
 * Runs at most once per browser (guarded by a localStorage flag).
 */
export async function migrateLocalStorageIfNeeded(): Promise<number> {
  if (localStorage.getItem(MIGRATION_FLAG) === "true") return 0;
  const legacy = readLegacyLocal();
  if (legacy.length === 0) {
    localStorage.setItem(MIGRATION_FLAG, "true");
    return 0;
  }
  const uid = await currentUserId();
  if (!uid) return 0; // try again next load when user is signed in
  const rows = legacy.map((ev) => ({
    id: ev.id,
    user_id: uid,
    title: ev.title,
    description: ev.description ?? null,
    start_date: ev.startDate,
    end_date: ev.endDate,
    start_time: ev.startTime || "00:00",
    end_time: ev.endTime || "23:59",
    color: ev.color || "blue",
  }));
  const { error } = await supabase
    .from("calendar_events")
    .upsert(rows, { onConflict: "id" });
  if (error) throw error;
  localStorage.setItem(MIGRATION_FLAG, "true");
  return rows.length;
}